// Pure address/listing helpers: name derivation, place-type icons, and
// Zillow/Redfin listing-URL parsing. No DOM, no network — unit-testable.

import type { ListingParse, Prediction } from './types';

export function defaultNameFromAddress(formattedAddress: string | undefined | null): string {
  if (!formattedAddress) return 'Untitled';
  const parts = formattedAddress.split(',').map((s) => s.trim()).filter(Boolean);
  return parts[0] || formattedAddress;
}

// True when an autocomplete prediction is a business / point of interest
// (a named place) rather than a plain street address.
export function predictionIsBusiness(p: Prediction | null | undefined): boolean {
  const t = p?.types || [];
  return t.includes('establishment') || t.includes('point_of_interest');
}

// Pick a friendly default pin name: a business keeps its name (e.g. "Blue
// Bottle Coffee"); a residential address falls back to the street line.
export function nameFromPrediction(
  p: Prediction | null | undefined,
  formattedAddress: string,
): string {
  if (p && predictionIsBusiness(p)) {
    const main = p.structured_formatting?.main_text;
    if (main) return main;
  }
  return defaultNameFromAddress(formattedAddress);
}

// A small emoji glyph chosen from a prediction's Places types. Free (no extra
// API call) and instant; grocery/cafe lead since those matter most for the
// score. Order matters — first match wins.
export function placeIcon(types: string[] | undefined): string {
  types = types || [];
  const MAP: [string, string][] = [
    ['supermarket', '🛒'], ['grocery_or_supermarket', '🛒'], ['liquor_store', '🍷'],
    ['cafe', '☕'], ['bakery', '🥐'], ['bar', '🍸'], ['night_club', '🍸'],
    ['meal_takeaway', '🥡'], ['restaurant', '🍴'], ['food', '🍴'],
    ['gym', '🏋️'], ['park', '🌳'], ['library', '📚'], ['book_store', '📚'],
    ['school', '🏫'], ['university', '🎓'], ['hospital', '🏥'], ['doctor', '🩺'],
    ['pharmacy', '💊'], ['shopping_mall', '🛍️'], ['clothing_store', '👗'],
    ['store', '🛍️'], ['airport', '✈️'], ['subway_station', '🚇'],
    ['train_station', '🚉'], ['transit_station', '🚉'], ['bus_station', '🚌'],
    ['church', '⛪'], ['place_of_worship', '🛐'], ['museum', '🏛️'],
    ['movie_theater', '🎬'], ['gas_station', '⛽'], ['parking', '🅿️'],
    ['bank', '🏦'], ['atm', '🏧'], ['lodging', '🛏️'], ['cemetery', '🪦'],
  ];
  for (const [t, ic] of MAP) if (types.includes(t)) return ic;
  if (types.includes('establishment')) return '🏢';
  return '📍'; // plain address
}

// ============ Zillow / Redfin listing URL parsing ============
const STREET_SUFFIXES = new Set([
  'aly', 'alley', 'ave', 'avenue', 'blvd', 'boulevard', 'cir', 'circle',
  'ct', 'court', 'dr', 'drive', 'hwy', 'highway', 'ln', 'lane', 'loop',
  'pkwy', 'parkway', 'pl', 'place', 'rd', 'road', 'sq', 'square', 'st',
  'street', 'ter', 'terrace', 'trl', 'trail', 'way',
]);

export function parseListingUrl(value: string): ListingParse | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (host === 'zillow.com' || host.endsWith('.zillow.com')) {
    return parseZillowListingPath(url.pathname);
  }
  if (host === 'redfin.com' || host.endsWith('.redfin.com')) {
    return parseRedfinListingPath(url.pathname);
  }
  return null;
}

export function parseZillowListingPath(pathname: string): ListingParse | null {
  const parts = decodedPathParts(pathname);
  const detailsIndex = parts.findIndex((p) => p.toLowerCase() === 'homedetails');
  if (detailsIndex === -1 || !parts[detailsIndex + 1]) return null;

  const tokens = slugTokens(parts[detailsIndex + 1]);
  const zipIndex = findZipIndex(tokens);
  if (zipIndex === -1) return addressFromStreetTokens(tokens);

  const zip = tokens[zipIndex];
  const stateIndex = zipIndex - 1;
  const stateCode = stateIndex >= 0 ? tokens[stateIndex].toUpperCase() : '';
  const locationTokens = tokens.slice(0, stateIndex);
  const streetEnd = findStreetSuffixIndex(locationTokens);
  const streetTokens = streetEnd >= 0 ? locationTokens.slice(0, streetEnd + 1) : locationTokens;
  const cityTokens = streetEnd >= 0 ? locationTokens.slice(streetEnd + 1) : [];

  const streetAddress = humanizeTokens(streetTokens);
  const city = humanizeTokens(cityTokens);
  const stateZip = [stateCode, zip].filter(Boolean).join(' ');
  const address = [streetAddress, city, stateZip].filter(Boolean).join(', ');
  return { address, streetAddress: streetAddress || address };
}

export function parseRedfinListingPath(pathname: string): ListingParse | null {
  const parts = decodedPathParts(pathname);
  const homeIndex = parts.findIndex((p) => p.toLowerCase() === 'home');
  const slug = homeIndex > 0 ? parts[homeIndex - 1] : parts[2];
  if (!slug) return null;

  const stateCode = /^[a-z]{2}$/i.test(parts[0] || '') ? parts[0].toUpperCase() : '';
  const city = parts[1] ? humanizeTokens(slugTokens(parts[1])) : '';
  const tokens = slugTokens(slug);
  const zipIndex = findZipIndex(tokens);
  const zip = zipIndex >= 0 ? tokens[zipIndex] : '';
  const streetTokens = zipIndex >= 0 ? tokens.slice(0, zipIndex) : tokens;
  const streetAddress = humanizeTokens(streetTokens);
  const stateZip = [stateCode, zip].filter(Boolean).join(' ');
  const address = [streetAddress, city, stateZip].filter(Boolean).join(', ');
  return { address, streetAddress: streetAddress || address };
}

function decodedPathParts(pathname: string): string[] {
  return pathname
    .split('/')
    .map((p) => {
      try {
        return decodeURIComponent(p);
      } catch {
        return p;
      }
    })
    .filter(Boolean);
}

function slugTokens(slug: string): string[] {
  return slug
    .replace(/_zpid$/i, '')
    .split(/[-_\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function findZipIndex(tokens: string[]): number {
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (/^\d{5}$/.test(tokens[i])) return i;
  }
  return -1;
}

function findStreetSuffixIndex(tokens: string[]): number {
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (STREET_SUFFIXES.has(tokens[i].toLowerCase().replace(/\./g, ''))) return i;
  }
  return -1;
}

function addressFromStreetTokens(tokens: string[]): ListingParse | null {
  const address = humanizeTokens(tokens);
  return address ? { address, streetAddress: address } : null;
}

function humanizeTokens(tokens: string[]): string {
  return tokens.join(' ').replace(/\s+/g, ' ').trim();
}
